<template>
  <div class="md:text-left w-full">
    <HeaderTitle>
      <template #top>{{ t("challenges.tab.title") }} </template>
      <template #bottom>{{ t("challenges.tab.subtitle") }} </template>
    </HeaderTitle>
    <FilterBar v-model:current-tab="currentTab" />
    <HeadlineElement v-if="filteredActiveChallenges.length > 0">
      {{ t("challenges.tab.own.challenges") }}
    </HeadlineElement>
    <ChallengeTileGrid
      v-if="filteredActiveChallenges.length > 0"
      :challenges="filteredActiveChallenges"
      grid-type="active"
    />
    <HistoryButton v-if="!isLoading" @click="redirectToHistory" />
    <HeadlineElement v-if="filteredAllChallenges.length > 0">
      {{ t("challenges.tab.all.challenges") }}
    </HeadlineElement>
    <ChallengeTileGrid
      v-if="filteredAllChallenges.length > 0"
      :challenges="filteredAllChallenges"
      grid-type="all"
    />
    <LoaderIcon v-if="isLoading" />
    <div
      v-else-if="
        filteredAllChallenges.length === 0 &&
        filteredActiveChallenges.length === 0
      "
      class="text-center mt-8"
    >
      {{ t("challenges.tab.empty.label") }}
    </div>
  </div>
</template>
<script setup lang="ts">
import HeaderTitle from "../../../components/v2/layout/header/HeaderTitle.vue";
import FilterBar from "../../../components/v2/challanges/FilterBar.vue";
import HeadlineElement from "../../../components/v2/layout/HeadlineElement.vue";
import ChallengeTileGrid from "../../../components/v2/challanges/ChallengeTileGrid.vue";
import type { CustomChallengeListViewDto } from "../../../dto/custom-challenge-list-view.dto.ts";
import {
  fetchActiveCustomChallenges,
  fetchAllCustomChallenges,
} from "../../../api/v2/custom-challenge.ts";
import {
  fetchActiveCompetitiveChallenges,
  fetchAllCompetitiveChallenges,
} from "../../../api/v2/competitive-challenge.ts";
import { ChallengeType } from "../../../enum/v2/challengeType.ts";
import { ActivityType } from "../../../enum/v2/activityType.ts";
import { useClientStore } from "../../../store/v2/client";
import { useUserStore } from "../../../store/v2/user";
import { useI18n } from "vue-i18n";
import { ChallengeTabType } from "../../../enum/v2/challenge-tab-type.ts";
import HistoryButton from "../../../components/v2/challanges/HistoryButton.vue";
import { useRouter } from "vue-router";
import LoaderIcon from "../../../components/v2/loader/LoaderIcon.vue";
const { t } = useI18n();
const router = useRouter();

const allChallenges = ref(<CustomChallengeListViewDto[]>[]);
const activeChallenges = ref(<CustomChallengeListViewDto[]>[]);
const client = useClientStore();
const user = useUserStore();
const now = new Date();
const currentTab = ref<ChallengeTabType>(null);
const isLoading = ref(true);

onMounted(async () => {
  try {
    await fetchAllChallenges();
    await fetchActiveChallenges();
  } finally {
    isLoading.value = false;
  }
});

async function fetchAllChallenges() {
  const [customRes, competitiveRes] = await Promise.all([
    fetchAllCustomChallenges(client.code),
    fetchAllCompetitiveChallenges(client.code),
  ]);

  const customChallenges = customRes.data.map((challenge) => ({
    ...challenge,
    category: ChallengeType.CUSTOM_CHALLENGE,
  }));

  const competitiveChallenges = competitiveRes.data.map((challenge) => ({
    ...challenge,
    category: ChallengeType.COMPETITIVE_CHALLENGE,
    activityType: challenge.competitiveChallengeType,
  }));

  allChallenges.value = [...customChallenges, ...competitiveChallenges]
    .filter((challenge) => {
      if (!challenge.endDate) {
        return false;
      }
      const endDate = new Date(challenge.endDate);
      return endDate.getTime() >= now.getTime();
    })
    .filter((challenge) => {
      if (!challenge.endDate) {
        return false;
      }
      const latestStartDate = new Date(challenge.latestStartDate);
      return latestStartDate.getTime() >= now.getTime();
    })
    .sort(
      (a, b) =>
        new Date(a.latestStartDate).getTime() -
        new Date(b.latestStartDate).getTime()
    );
}

async function fetchActiveChallenges() {
  const [customRes, competitiveRes] = await Promise.all([
    fetchActiveCustomChallenges(user.externalUserId),
    fetchActiveCompetitiveChallenges(user.externalUserId),
  ]);

  const activeCustomChallenges = customRes.data.map((challenge) => ({
    ...challenge,
    category: ChallengeType.CUSTOM_CHALLENGE,
  }));

  const activeCompetitiveChallenges = competitiveRes.data.map((challenge) => ({
    ...challenge,
    category: ChallengeType.COMPETITIVE_CHALLENGE,
    activityType: challenge.competitiveChallengeType,
  }));

  activeChallenges.value = [
    ...activeCustomChallenges,
    ...activeCompetitiveChallenges,
  ]
    .filter((challenge) => {
      if (!challenge.endDate) {
        return false;
      }
      const endDate = new Date(challenge.endDate);
      return endDate.getTime() >= now.getTime();
    })
    .sort(
      (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
    );
}

const filteredAllChallenges = computed(() => {
  const activeIds = new Set(
    activeChallenges.value.map((challenge) => {
      if (challenge.category === ChallengeType.COMPETITIVE_CHALLENGE) {
        return challenge.mappingId;
      } else {
        return challenge.id;
      }
    })
  );

  let filtered = allChallenges.value.filter((challenge) => {
    if (activeIds.has(challenge.id)) {
      return false;
    }
    return true;
  });

  if (currentTab.value === ChallengeTabType.TAB_BIKE) {
    return filtered.filter(
      (challenge) =>
        challenge.activityType ===
          ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_BICYCLE ||
        challenge.activityType ===
          ActivityType.COMPETITIVE_CHALLENGE_TYPE_BICYCLE
    );
  } else if (currentTab.value === ChallengeTabType.TAB_WALK) {
    return filtered.filter(
      (challenge) =>
        challenge.activityType ===
          ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_STEPS ||
        challenge.activityType === ActivityType.COMPETITIVE_CHALLENGE_TYPE_STEPS
    );
  } else if (currentTab.value === ChallengeTabType.TAB_MORE) {
    return filtered.filter(
      (challenge) =>
        challenge.activityType ===
          ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_STANDARD ||
        challenge.activityType ===
          ActivityType.COMPETITIVE_CHALLENGE_TYPE_STANDARD
    );
  }

  return filtered;
});

const filteredActiveChallenges = computed(() => {
  if (currentTab.value === ChallengeTabType.TAB_BIKE) {
    return activeChallenges.value.filter(
      (challenge) =>
        challenge.activityType ===
          ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_BICYCLE ||
        challenge.activityType ===
          ActivityType.COMPETITIVE_CHALLENGE_TYPE_BICYCLE
    );
  } else if (currentTab.value === ChallengeTabType.TAB_WALK) {
    return activeChallenges.value.filter(
      (challenge) =>
        challenge.activityType ===
          ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_STEPS ||
        challenge.activityType === ActivityType.COMPETITIVE_CHALLENGE_TYPE_STEPS
    );
  } else if (currentTab.value === ChallengeTabType.TAB_MORE) {
    return activeChallenges.value.filter(
      (challenge) =>
        challenge.activityType ===
          ActivityType.CUSTOM_CHALLENGE_ACTIVITY_TYPE_STANDARD ||
        challenge.activityType ===
          ActivityType.COMPETITIVE_CHALLENGE_TYPE_STANDARD
    );
  }

  return activeChallenges.value;
});

function redirectToHistory() {
  router.push({ name: "challengesHistory" });
}
</script>
